import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {
    Typography,
    Checkbox,
} from '@mui/material';
import {TreeView, TreeItem} from '@mui/lab';
import {ExpandMore, ChevronRight} from '@mui/icons-material';
import {decryptPasswordLambda} from '../../../utils/functionSignatures';
import {BrickContext} from 'olympe';

/**
 * A dropdown allowing the user to select an available Candex market.
 * @param {CandexContent.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function CandexMarketsDropdown(props) {
    const [selectedChild, setSelectedChild] = useState(null);
    const [groupedData, setGroupedData] = useState({});

    useEffect(() => {
        try {
            const tableData = JSON.parse(props.candexTableData) || [];
            let authorizedCodes = props.companyCodesAuthorized;
            if (typeof authorizedCodes === 'string') {
                authorizedCodes = JSON.parse(authorizedCodes);
            }

            let filteredData = tableData;
            if (!authorizedCodes.includes('*')) {
                filteredData = tableData.filter((entry) =>
                    authorizedCodes.includes(entry.company_code),
                );
            }

            const groups = {};
            filteredData.forEach((entry) => {
                const {country} = entry;
                if (!groups[country]) {
                    groups[country] = [];
                }
                groups[country].push(entry);
            });
            setGroupedData(groups);
        } catch (error) {
            console.error('Failed to parse Candex data:', error);
        }
    }, [props.candexTableData, props.companyCodesAuthorized]);

    const handleSelectChildCode = async (entry) => {
        if (
            selectedChild &&
            selectedChild.country === entry.country &&
            selectedChild.company_code === entry.company_code
        ) {
            setSelectedChild(null);
            if (props.onChange) {
                props.onChange(null);
            }
            return;
        }
        let decryptedPassword = '';
        if (entry?.password) {
            decryptedPassword = await decryptPasswordLambda(
                props.context,
                props.decryptPassword,
                entry.password,
            );
        }
        setSelectedChild(entry);
        if (props.onChange) {
            props.onChange({
                label: entry.country,
                company_code: entry.company_code,
                username: entry.username,
                decryptedPassword: decryptedPassword[0],
                url: entry.url,
            });
        }
    };

    const handleMarketCheckboxClick = (country) => {
        if (selectedChild?.country === country) {
            setSelectedChild(null);
            if (props.onChange) {
                props.onChange(null);
            }
        }
    };

    const isMarketChecked = (country) => {
        return selectedChild?.country === country;
    };

    const isChildChecked = (entry) => {
        return (
            selectedChild?.country === entry.country &&
            selectedChild?.company_code === entry.company_code
        );
    };

    return (
        <div style={{width: '25rem'}}>
            <Typography variant="subtitle1" sx={{mb: 1}}>
                Select Country
            </Typography>

            <TreeView
                defaultCollapseIcon={<ExpandMore />}
                defaultExpandIcon={<ChevronRight />}
                sx={{
                    borderRadius: 1,
                    p: 1,
                    backgroundColor: '#fff',
                    width: '100%',
                    maxHeight: 200,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
                className={'popup-scroller'}
            >
                {Object.entries(groupedData).map(([country, entries]) => (
                    <TreeItem
                        key={country}
                        nodeId={country}
                        label={
                            <div style={{display: 'flex', alignItems: 'center'}}>
                                <Checkbox
                                    checked={isMarketChecked(country)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleMarketCheckboxClick(country);
                                    }}
                                />
                                <span>{country}</span>
                            </div>
                        }
                    >
                        {entries.map((entry, idx) => {
                            const nodeId = `${country}-${entry.company_code}-${idx}`;
                            return (
                                <TreeItem
                                    key={nodeId}
                                    nodeId={nodeId}
                                    label={
                                        <div
                                            style={{display: 'flex', alignItems: 'center'}}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelectChildCode(entry);
                                            }}
                                        >
                                            <Checkbox
                                                checked={isChildChecked(entry)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSelectChildCode(entry);
                                                }}
                                            />
                                            <span>{entry.company_code}</span>
                                        </div>
                                    }
                                />
                            );
                        })}
                    </TreeItem>
                ))}
            </TreeView>
        </div>
    );
}

CandexMarketsDropdown.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    candexTableData: PropTypes.string.isRequired,
    decryptPassword: PropTypes.object.isRequired,
    companyCodesAuthorized: PropTypes.array,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    selectedValue: PropTypes.object,
};
